import { useEffect, useMemo, useState, type ReactNode } from "react";
import { IconButton, TextField, vars } from "@pack-it-up/ui-web";
import { createRapidAdd, type ShortcutRegistry } from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import type { Person } from "./people.js";
import { CancelIcon, CheckIcon, PlusIcon } from "../../app/icons.js";
import { ItemComposer, type ItemComposerDraft } from "./ItemComposer.js";
import * as styles from "./AddRow.css.js";

// Rapid-add affordance (Doc 06 §5): a dashed/plus row that, once activated, becomes a
// composer — Add/Enter commits and reopens a blank (focus kept) for fast successive
// adds; Esc discards. `createRapidAdd` only tracks idle/composing mode plus a bare name
// draft; the item variant renders the full `ItemComposer` (T-G) and manages its own
// qty/assignment/notes locally, only borrowing the store for mode + the discard
// transition. The item composer is passed `blurBehavior="discard"` so clicking away
// discards the draft (restoring the original add-flow semantics) while Enter/Add still
// commits — see `ItemComposer`'s `blurBehavior` prop.
export const AddRow = ({
  label,
  onAdd,
  variant = "plus",
  people,
  tagPalette,
  onAddItem,
  itemVariant = "item",
  shortcutRegistry,
}: {
  label: string;
  onAdd: (name: string) => void;
  variant?: "plus" | "dashed";
  // Item-composer wiring — required when variant is "plus" and the caller wants the
  // full kit composer (qty/assignment/notes). Falls back to the bare text field if
  // omitted, so any future non-item "plus" usage keeps working unchanged.
  // Assignable people for the add composer: trip members, or a template's participants.
  people?: Person[];
  // Tags already in use on this trip/template (T29), offered as one-click chips.
  tagPalette?: string[];
  onAddItem?: (draft: ItemComposerDraft) => void;
  // "todo" swaps the composer's qty stepper for a due-date field.
  itemVariant?: "item" | "todo";
  // Doc 06 §8 — when provided, binds Cmd/Ctrl+Enter to open this add row's composer
  // (rapid-add `begin`, the same command as clicking the row). The page passes it on
  // the "current" (first) category only.
  shortcutRegistry?: ShortcutRegistry;
}): ReactNode => {
  const rapid = useMemo(() => createRapidAdd(), []);
  useEffect(() => {
    if (shortcutRegistry === undefined) return undefined;
    return shortcutRegistry.register("editor", { "cmd+enter": () => rapid.getState().begin() });
  }, [shortcutRegistry, rapid]);
  const mode = useStoreValue(rapid, (s) => s.mode);
  const draft = useStoreValue(rapid, (s) => s.draft);
  // Forces ItemComposer to remount with blank state after each rapid add (Enter/Add
  // commits and reopens blank with focus kept — the composer has no other way to reset
  // its internal name/qty/assignment/notes state on repeat commits).
  const [addKey, setAddKey] = useState(0);

  const commit = (): void => {
    const r = rapid.getState().commit();
    // Unlike the item composer below, a saved category closes back to the "+" row
    // rather than reopening blank — categories are a one-off per creation, not a
    // rapid-add flow.
    if (r.kind === "create") {
      onAdd(r.name);
      rapid.getState().discard();
    }
  };

  if (mode === "composing") {
    if (variant === "plus" && people !== undefined && onAddItem !== undefined) {
      return (
        <ItemComposer
          key={addKey}
          ariaLabel={label}
          people={people}
          primaryLabel="Add item"
          variant={itemVariant}
          inset
          blurBehavior="discard"
          initial={{ name: "", quantity: 1, assignment: { kind: "everyone" }, notes: "", dueDate: "", tags: [] }}
          {...(tagPalette !== undefined ? { tagPalette } : {})}
          onCommit={(d) => {
            if (d.name.trim() === "") return;
            onAddItem(d);
            setAddKey((k) => k + 1); // remount blank, autoFocus reclaims focus
          }}
          onCancel={() => rapid.getState().discard()}
        />
      );
    }
    const discard = (): void => rapid.getState().discard();
    return (
      <div style={{ margin: `${vars.space.xs} 0`, display: "flex", flexDirection: "column", gap: vars.space.xs }}>
        <div style={{ display: "flex", alignItems: "center", gap: vars.space.xs }}>
          <div style={{ flex: 1 }}>
            <TextField
              label=""
              aria-label={label}
              placeholder={label}
              value={draft}
              autoFocus
              onChange={(v) => rapid.getState().setDraft(v)}
              onBlur={discard}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") discard();
              }}
            />
          </div>
          {/* Explicit save/cancel (Enter/Esc still work). onMouseDown preventDefault
              keeps the field's blur-discard from firing before the click lands. */}
          <span onMouseDown={(ev) => ev.preventDefault()}>
            <IconButton label="Save category" onClick={commit} style={{ color: vars.color.success }}>
              {CheckIcon}
            </IconButton>
          </span>
          <span onMouseDown={(ev) => ev.preventDefault()}>
            <IconButton label="Cancel adding category" onClick={discard} style={{ color: vars.color.danger }}>
              {CancelIcon}
            </IconButton>
          </span>
        </div>
      </div>
    );
  }

  if (variant === "dashed") {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={() => rapid.getState().begin()}
        className={styles.dashedButton}
      >
        {PlusIcon(14)}
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => rapid.getState().begin()}
      className={styles.plusButton}
    >
      {PlusIcon()}
      {label}
    </button>
  );
};
